import { MarkdownToJSX } from 'markdown-to-jsx';
import React from 'react';

import { cn } from '@/lib/utils';

import { CodeBlock } from '@/decodo/blocks/CodeBlock';
import Heading from '@/decodo/components/atoms/Heading';
import NextImage from '@/decodo/components/atoms/Image';
import LinkButton from '@/decodo/components/atoms/LinkButton';

const retainContextSizeClass = 'text-base';

export const buildMarkdownOptions = (
  classNames: any,
  extraProps: any
): MarkdownToJSX.Options => ({
  createElement: (tag, props, children) => {
    // eslint-disable-next-line
    // @ts-ignore
    // The `createElement` function passes a default `className` prop which overrides the `classNames` prop
    // in the our custom components. This is a workaround to avoid that.
    const { className, classNames, ...restProps } = props;
    if (classNames) {
      // eslint-disable-next-line
      // @ts-ignore
      restProps['classNames'] = classNames;
    } else {
      // eslint-disable-next-line
      // @ts-ignore
      restProps['className'] = className;
    }

    return React.createElement(tag, restProps, children);
  },
  overrides: {
    h1: {
      component: Heading,
      props: {
        level: 'h1',
        'data-tracker-context': 'rich-text-field-heading-h1',
        classNames: {
          root: cn('w-full', classNames?.heading?.h1?.root),
        },
        ...extraProps?.heading?.h1,
      },
    },
    h2: {
      component: Heading,
      props: {
        level: 'h2',
        'data-tracker-context': 'rich-text-field-heading-h2',
        classNames: {
          root: cn('w-full', classNames?.heading?.h2?.root),
        },
        ...extraProps?.heading?.h2,
      },
    },
    h3: {
      component: Heading,
      props: {
        level: 'h3',
        'data-tracker-context': 'rich-text-field-heading-h3',
        classNames: {
          root: cn('w-full', classNames?.heading?.h3?.root),
        },
        ...extraProps?.heading?.h3,
      },
    },
    h4: {
      component: Heading,
      props: {
        level: 'h4',
        'data-tracker-context': 'rich-text-field-heading-h4',
        classNames: {
          root: cn('w-full', classNames?.heading?.h4?.root),
        },
        ...extraProps?.heading?.h4,
      },
    },
    h5: {
      component: Heading,
      props: {
        level: 'h5',
        'data-tracker-context': 'rich-text-field-heading-h5',
        classNames: {
          root: cn('w-full', classNames?.heading?.h5?.root),
        },
        ...extraProps?.heading?.h5,
      },
    },
    h6: {
      component: Heading,
      props: {
        level: 'h6',
        'data-tracker-context': 'rich-text-field-heading-h6',
        classNames: {
          root: cn('w-full', classNames?.heading?.h6?.root),
        },
        ...extraProps?.heading?.h6,
      },
    },
    p: {
      component: 'p',
      props: {
        className: cn(classNames?.paragraph),
        'data-tracker-context': 'rich-text-field-paragraph',
        ...extraProps?.paragraph,
      },
    },
    ol: {
      component: 'ol',
      props: {
        className: cn(
          'ml-5 flex list-outside flex-col gap-5 text-left',
          'list-decimal',
          classNames?.listOrdered
        ),
        'data-tracker-context': 'rich-text-field-list-ordered',
        ...extraProps?.listOrdered,
      },
    },
    ul: {
      component: 'ul',
      props: {
        className: cn(
          'ml-5 flex list-outside flex-col gap-5 text-left',
          'list-disc',
          classNames?.listUnordered
        ),
        'data-tracker-context': 'rich-text-field-list-unordered',
        ...extraProps?.listUnordered,
      },
    },
    blockquote: {
      component: 'blockquote',
      props: {
        className: cn('border-l-2 border-gray-300 pl-4', classNames?.quote),
        'data-tracker-context': 'rich-text-field-quote',
        ...extraProps?.quote,
      },
    },
    a: {
      component: LinkButton,
      props: {
        classNames: {
          ...classNames?.link,
          root: cn(
            'hover:underline word-break',
            retainContextSizeClass,
            classNames?.link?.root
          ),
        },
        variant: 'text',
        color: 'tertiary',
        slim: true,
        'data-tracker-context': 'rich-text-field-link',
        ...extraProps?.link,
      },
    },
    img: {
      component: NextImage,
      props: {
        className: cn(classNames?.image),
        'data-tracker-context': 'rich-text-field-image',
        ...extraProps?.image,
      },
    },
    pre: {
      component: (props) => {
        return (
          <CodeBlock
            items={[
              {
                // This is how the plugin passes the code
                code: props.children.props.children,
                language: 'Python',
              },
            ]}
          />
        );
      },
      props: {
        'data-tracker-context': 'rich-text-field-code',
        ...extraProps?.code,
      },
    },
    strong: {
      component: 'strong',
      props: {
        className: cn('bold'),
        'data-tracker-context': 'rich-text-field-bold',
      },
    },
    em: {
      component: 'span',
      props: {
        className: cn('italic'),
        'data-tracker-context': 'rich-text-field-italic',
      },
    },
    u: {
      component: 'span',
      props: {
        className: cn('underline'),
        'data-tracker-context': 'rich-text-field-underline',
      },
    },
    del: {
      component: 'span',
      props: {
        className: cn('line-through'),
        'data-tracker-context': 'rich-text-field-strikethrough',
      },
    },
    code: {
      component: 'code',
      props: {
        'data-tracker-context': 'rich-text-field-code',
      },
    },
    sup: {
      component: 'sup',
      props: {
        className: cn('text-xs'),
        'data-tracker-context': 'rich-text-field-sup',
      },
    },
  },
});
